// aifab - ModelCatalog
// Lists all 23 model families with purpose support, best use case, and examples.
import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../hooks/apiFetch';
import { Spinner } from '../components/common';

const lbl = { display:'block', fontSize:11, fontWeight:700, color:'#475569', textTransform:'uppercase',
  letterSpacing:'.05em', margin:'0 0 4px' };
const inp = { width:'100%', boxSizing:'border-box', padding:'8px 10px', fontSize:13, marginBottom:14,
  border:'1px solid var(--bd)', borderRadius:6, fontFamily:'inherit' };

const PURPOSE_COLORS = {  CLASSIFICATION:  { bg:'rgba(232,71,10,.1)',  tx:'#fb923c', bd:'rgba(232,71,10,.2)'  },
  REGRESSION:      { bg:'rgba(37,99,235,.1)',   tx:'#60a5fa', bd:'rgba(37,99,235,.2)'  },
  DOMAIN_PRETRAIN: { bg:'rgba(52,211,153,.1)',  tx:'#34d399', bd:'rgba(52,211,153,.2)' },
  NER:             { bg:'rgba(139,92,246,.1)',  tx:'#a78bfa', bd:'rgba(139,92,246,.2)' },
  CUSTOM:          { bg:'rgba(107,114,148,.1)', tx:'#94a3b8', bd:'rgba(107,114,148,.2)'},
};

const PURPOSE_BADGE = ({ code }) => {
  const c = PURPOSE_COLORS[code] || PURPOSE_COLORS.CUSTOM;
  return (
    <span style={{ fontSize:9, fontWeight:700, letterSpacing:'.07em', textTransform:'uppercase',
      padding:'2px 7px', borderRadius:3, background:c.bg, color:c.tx, border:`1px solid ${c.bd}`,
      fontFamily:'var(--f-mono)', whiteSpace:'nowrap' }}>
      {code}
    </span>
  );
};

// Static catalog - augments API data with use cases and examples
const CATALOG = {
  DISTILBERT: {
    path: 'Transformer',
    headline: 'Fast text classification on CPU.',
    description: 'DistilBERT is 40% smaller and 60% faster than BERT Base while retaining 97% of its performance. The best starting point for classification on CPU servers.',
    purposes: ['CLASSIFICATION', 'DOMAIN_PRETRAIN', 'NER'],
    bestFor: 'Most classification tasks on CPU. Domain pre-training on moderate-sized corpora (10k-100k rows).',
    examples: ['ITSM ticket routing', 'Email intent detection', 'Support request categorisation', 'Domain-specific vocabulary adaptation'],
    requirements: 'CPU or GPU. ~260MB disk.',
  },
  BERT_BASE: {
    path: 'Transformer',
    headline: 'Strong contextual representations for complex text.',
    description: 'The original BERT model. Better than DistilBERT on tasks where full context matters, at higher compute cost.',
    purposes: ['CLASSIFICATION', 'DOMAIN_PRETRAIN', 'NER'],
    bestFor: 'Tasks where sentence-level context is important. Long-form documents where truncation hurts quality.',
    examples: ['Legal document classification', 'Clinical note coding', 'Multi-class sentiment analysis'],
    requirements: 'CPU (slow) or GPU recommended. ~440MB disk.',
  },
  ROBERTA: {
    path: 'Transformer',
    headline: 'BERT trained longer with more data - often beats BERT Base.',
    description: 'RoBERTa removes the next-sentence prediction objective and trains with larger batches and more data. Frequently outperforms BERT Base on classification benchmarks.',
    purposes: ['CLASSIFICATION', 'NER'],
    bestFor: 'When BERT Base accuracy is not sufficient. Good on noisy or informal text.',
    examples: ['Social media classification', 'Customer feedback analysis', 'Contract clause classification'],
    requirements: 'GPU recommended. ~480MB disk.',
  },
  ALBERT: {
    path: 'Transformer',
    headline: 'Parameter-efficient encoder - less memory, competitive accuracy.',
    description: 'ALBERT uses parameter sharing across layers, reducing memory footprint significantly while maintaining competitive accuracy.',
    purposes: ['CLASSIFICATION', 'NER'],
    bestFor: 'Memory-constrained servers where RoBERTa is too large.',
    examples: ['Ticket classification on low-memory servers', 'Document classification'],
    requirements: 'CPU or GPU. ~48MB disk (base).',
  },
  MINILM: {
    path: 'Transformer',
    headline: 'Fast, compact sentence embeddings.',
    description: 'MiniLM produces dense sentence-level embeddings optimised for semantic similarity tasks. Very fast at inference.',
    purposes: ['CLASSIFICATION'],
    bestFor: 'Semantic similarity, duplicate detection, semantic search.',
    examples: ['Duplicate ticket detection', 'Semantic document clustering', 'Similar article retrieval'],
    requirements: 'CPU-friendly. ~120MB disk.',
  },
  BGE_M3: {
    path: 'Transformer',
    headline: 'Multilingual, multi-granularity embeddings.',
    description: 'BGE-M3 supports 100+ languages and multiple retrieval modes (dense, sparse, multi-vector). State-of-the-art on multilingual retrieval benchmarks.',
    purposes: ['CLASSIFICATION'],
    bestFor: 'Multilingual text. Semantic retrieval across languages.',
    examples: ['Cross-lingual support ticket matching', 'Multilingual document classification'],
    requirements: 'GPU recommended. ~2.2GB disk.',
  },
  E5_LARGE: {
    path: 'Transformer',
    headline: 'High-quality English embeddings for retrieval and similarity.',
    description: 'E5-Large produces high-quality English embeddings trained specifically for retrieval tasks with instruction prefix support.',
    purposes: ['CLASSIFICATION'],
    bestFor: 'High-quality English semantic search and similarity.',
    examples: ['Knowledge base search', 'Policy document retrieval', 'Regulatory clause matching'],
    requirements: 'GPU recommended. ~1.3GB disk.',
  },
  XGBOOST: {
    path: 'Classical ML',
    headline: 'Gradient boosted trees. Fast and interpretable.',
    description: 'XGBoost is one of the most reliable models for tabular data and text features combined. Trains in minutes, easy to tune.',
    purposes: ['CLASSIFICATION', 'REGRESSION'],
    bestFor: 'Tabular data with text features. When you need fast results and interpretability.',
    examples: ['Priority prediction from structured + text features', 'Incident SLA breach prediction', 'Customer churn prediction'],
    requirements: 'CPU only. No GPU needed.',
  },
  LIGHTGBM: {
    path: 'Classical ML',
    headline: 'Faster than XGBoost on large datasets.',
    description: 'LightGBM uses leaf-wise tree growth and histogram-based algorithms. Often faster than XGBoost on large datasets with similar accuracy.',
    purposes: ['CLASSIFICATION', 'REGRESSION'],
    bestFor: 'Large tabular datasets (100k+ rows). When XGBoost is too slow.',
    examples: ['Large-scale ticket triage', 'Real-time risk scoring'],
    requirements: 'CPU only.',
  },
  CATBOOST: {
    path: 'Classical ML',
    headline: 'Handles categorical features natively.',
    description: 'CatBoost applies ordered target statistics for categorical features without manual encoding, reducing preprocessing effort.',
    purposes: ['CLASSIFICATION', 'REGRESSION'],
    bestFor: 'Datasets with many categorical columns. Reduces feature engineering work.',
    examples: ['Team/category/priority feature prediction', 'Product classification with many categories'],
    requirements: 'CPU only.',
  },
  RANDOM_FOREST: {
    path: 'Classical ML',
    headline: 'Robust baseline that handles noise well.',
    description: 'An ensemble of decision trees. Less sensitive to hyperparameters than boosted trees and provides natural feature importance scores.',
    purposes: ['CLASSIFICATION', 'REGRESSION'],
    bestFor: 'Baseline models. When interpretability matters (feature importances).',
    examples: ['Feature importance analysis', 'Baseline classification before BERT fine-tuning'],
    requirements: 'CPU only.',
  },
  LOGISTIC_REGRESSION: {
    path: 'Classical ML',
    headline: 'Fast, linear, easy to explain.',
    description: 'Logistic regression with TF-IDF features is a strong baseline for text classification. Fully interpretable and fast to train.',
    purposes: ['CLASSIFICATION'],
    bestFor: 'Simple text classification. When you need to explain every prediction. Production with low latency requirements.',
    examples: ['Binary spam/not-spam', 'Simple intent detection', 'Regulatory-compliance-constrained environments'],
    requirements: 'CPU only.',
  },
  SVM: {
    path: 'Classical ML',
    headline: 'Strong on high-dimensional text features.',
    description: 'Support Vector Machines with TF-IDF work well for text classification, especially with limited data.',
    purposes: ['CLASSIFICATION'],
    bestFor: 'Limited training data. High-dimensional sparse feature spaces.',
    examples: ['Document classification with small datasets', 'Technical support category routing'],
    requirements: 'CPU only.',
  },
  MISTRAL_7B: {
    path: 'LLM',
    headline: 'Strong 7B instruction-following model.',
    description: 'Mistral 7B is one of the best open-weight 7B models. Fine-tuning via LoRA/4-bit quantisation reduces GPU memory requirements significantly.',
    purposes: ['CUSTOM'],
    bestFor: 'Instruction following, structured output generation, complex reasoning tasks.',
    examples: ['Automated incident report generation', 'Structured data extraction from free text', 'Custom NLP workflows'],
    requirements: 'GPU required (16GB+ VRAM recommended). 4-bit: ~5GB VRAM.',
  },
  LLAMA3_8B: {
    path: 'LLM',
    headline: 'Meta\'s LLaMA 3 - strong reasoning and instruction following.',
    description: 'LLaMA 3 8B offers strong performance on reasoning and instruction tasks with good community support.',
    purposes: ['CUSTOM'],
    bestFor: 'General-purpose LLM fine-tuning. Reasoning-heavy tasks.',
    examples: ['Automated response drafting', 'Multi-step reasoning workflows', 'Knowledge-grounded generation'],
    requirements: 'GPU required. 4-bit: ~5GB VRAM.',
  },
  PHI3_MINI: {
    path: 'LLM',
    headline: 'Small but capable. Runs on modest hardware.',
    description: 'Phi-3 Mini is a 3.8B model that punches above its weight on reasoning tasks. Can run on smaller GPU servers.',
    purposes: ['CUSTOM'],
    bestFor: 'When GPU memory is limited. Tasks requiring structured reasoning on small inputs.',
    examples: ['Structured field extraction', 'Short-form generation', 'Code generation assistance'],
    requirements: 'GPU recommended. 4-bit: ~2.5GB VRAM.',
  },
  QWEN25: {
    path: 'LLM',
    headline: 'Strong multilingual LLM from Alibaba.',
    description: 'Qwen 2.5 offers competitive multilingual performance and strong code and math capabilities.',
    purposes: ['CUSTOM'],
    bestFor: 'Multilingual generation. Code and structured output tasks.',
    examples: ['Multilingual report generation', 'Code review assistance', 'Cross-language extraction'],
    requirements: 'GPU required. 4-bit: ~5GB VRAM.',
  },
  GEMMA2: {
    path: 'LLM',
    headline: 'Google\'s efficient open model - strong on text tasks.',
    description: 'Gemma 2 from Google. Competitive with Mistral 7B on text understanding and generation tasks.',
    purposes: ['CUSTOM'],
    bestFor: 'Text generation and comprehension. Strong alternative to Mistral.',
    examples: ['Document summarisation', 'Q&A generation', 'Content classification with generation'],
    requirements: 'GPU required. 4-bit: ~5GB VRAM.',
  },
  DEEPSEEK_R1_7B: {
    path: 'LLM',
    headline: 'Best open reasoning model at 7B. Logic, math, code.',
    description: 'DeepSeek-R1-Distill-Qwen-7B is a dense 7B model distilled from DeepSeek-R1\'s reasoning traces onto a Qwen 2.5 backbone. Outperforms many models 4x its size on reasoning benchmarks. Apache 2.0.',
    purposes: ['CUSTOM'],
    bestFor: 'Reasoning-intensive tasks. Code generation. Math and logic. Fine-tune on domain-specific problem-answer pairs.',
    examples: ['Automated root cause analysis', 'Step-by-step troubleshooting guidance', 'Code review and bug explanation', 'Structured reasoning over incident data'],
    requirements: 'GPU required (16GB+ VRAM). 4-bit: ~5GB VRAM.',
  },
  DEEPSEEK_R1_8B: {
    path: 'LLM',
    headline: 'R1 reasoning distilled into LLaMA 3 8B architecture.',
    description: 'DeepSeek-R1-Distill-Llama-8B applies R1 reasoning traces to a LLaMA 3 8B backbone. Identical reasoning quality to the 7B variant but in the LLaMA architecture - preferred when your toolchain is LLaMA-native. Apache 2.0.',
    purposes: ['CUSTOM'],
    bestFor: 'Same use cases as R1-7B. Choose this when LLaMA architecture compatibility matters.',
    examples: ['Complex multi-step reasoning', 'Instruction-following with chain-of-thought', 'Technical explanation generation'],
    requirements: 'GPU required (16GB+ VRAM). 4-bit: ~5GB VRAM.',
  },
  QWEN25_CODER: {
    path: 'LLM',
    headline: 'Best open coding model at 7B. 88.4% HumanEval.',
    description: 'Qwen2.5-Coder-7B-Instruct achieves 88.4% on HumanEval, matching or beating GPT-4o on code benchmarks at 7B. Fine-tune on your codebase, API docs, or runbooks. Apache 2.0.',
    purposes: ['CUSTOM'],
    bestFor: 'Code generation, completion, and explanation. Fine-tuning on internal codebases or technical documentation.',
    examples: ['Internal code assistant fine-tuned on your stack', 'Runbook automation from natural language', 'PR description generation', 'Technical doc Q&A'],
    requirements: 'GPU required (16GB+ VRAM). 4-bit: ~5GB VRAM.',
  },
  DEEPSEEK_CODER: {
    path: 'LLM',
    headline: 'MoE coding model - 16B total, 2.4B active. Strong code generation.',
    description: 'DeepSeek-Coder-V2-Lite is a 16B Mixture-of-Experts model with only 2.4B active parameters per token. Strong code generation and completion at lower inference cost than dense 7B models.',
    purposes: ['CUSTOM'],
    bestFor: 'Code generation and completion. Efficient inference via MoE. Cost-effective alternative to dense coding models.',
    examples: ['Code completion and inline suggestions', 'Multi-language code translation', 'Automated test generation', 'API integration scaffolding'],
    requirements: 'GPU required (16GB+ VRAM for MoE routing). 4-bit: ~8GB VRAM.',
  },
  KIMI_K2: {
    path: 'LLM',
    headline: 'Kimi K2 (2026) - MIT licensed MoE. Agentic coding and reasoning.',
    description: 'Kimi K2 is a 1-trillion parameter Mixture-of-Experts model released in 2026 by Moonshot AI under MIT licence. Designed for agentic tasks - tool use, multi-step planning, and coding. Too large for end-to-end fine-tuning; use via CUSTOM mode with the LLM plugin.',
    purposes: ['CUSTOM'],
    bestFor: 'Agentic workflows. Complex multi-step coding and reasoning. When you need a frontier-class model with an open licence.',
    examples: ['Autonomous code refactoring agent', 'Multi-step incident investigation', 'Tool-augmented task execution', 'Complex document analysis pipelines'],
    requirements: 'GPU server with 24GB+ VRAM required. ~20GB quantized model weight.',
  },
};

const PATH_ORDER = ['Transformer', 'Classical ML', 'LLM'];

export default function ModelCatalog() {
  const { data: families, loading } = useApi('/models/families');
  const { data: customModels, reload: reloadCustom } = useApi('/models/custom');

  let _me = {};
  try { _me = JSON.parse(localStorage.getItem('aifab_user') || '{}'); } catch { _me = {}; }
  const canManage = ['ADMIN', 'ORG_ADMIN'].includes(_me.role);

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ family_code:'', model_name:'', source_url:'', source_type:'HUGGINGFACE', description:'' });
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState(null);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submitModel = async () => {
    setFormErr(null);
    const id = (form.model_name || '').trim();
    if (!form.family_code) { setFormErr('Select a family.'); return; }
    if (!/^[\w./-]{1,200}$/.test(id)) { setFormErr("Identifier: 1-200 chars of letters, digits, '_', '.', '/', '-'."); return; }
    if (!(form.source_url || '').trim()) { setFormErr('Enter where the model is hosted.'); return; }
    setSaving(true);
    try {
      await apiFetch('/models/custom', { method:'POST', body: JSON.stringify(form) });
      setShowAdd(false);
      setForm({ family_code:'', model_name:'', source_url:'', source_type:'HUGGINGFACE', description:'' });
      reloadCustom();
    } catch (e) {
      if (e.code === 'SESSION_EXPIRED') return;
      setFormErr(e.message || 'Could not add model.');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    try {
      await apiFetch(`/models/custom/${id}/deactivate`, { method:'PATCH' });
      reloadCustom();
    } catch (e) {
      if (e.code !== 'SESSION_EXPIRED') alert(e.message || 'Could not deactivate.');
    }
  };

  if (loading) return (
    <div className="page" style={{ display:'flex', justifyContent:'center', paddingTop:80 }}>
      <Spinner/>
    </div>
  );

  const familyMap = {};
  (families || []).forEach(f => { familyMap[f.family_code] = f; });

  const grouped = {};
  PATH_ORDER.forEach(p => { grouped[p] = []; });

  Object.entries(CATALOG).forEach(([code, info]) => {
    const path = info.path || 'Other';
    if (!grouped[path]) grouped[path] = [];
    grouped[path].push({ code, ...info, ...familyMap[code] });
  });

  return (
    <div className="page">
      <div style={{ marginBottom:24, display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16 }}>
        <p style={{ fontSize:13, color:'#64748B', maxWidth:600, margin:0 }}>
          23 model families across Transformer, Classical ML, and LLM paths.
          Includes DeepSeek R1 distills, Qwen2.5-Coder, Kimi K2, and more.
          Each family supports specific training modes - shown as purpose badges.
          LLM families require the CUSTOM mode and a GPU server.
        </p>
        {canManage && (
          <button onClick={() => { setFormErr(null); setShowAdd(true); }}
            style={{ flexShrink:0, background:'#E8470A', color:'#fff', border:'none', borderRadius:6,
              padding:'9px 16px', fontSize:13, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
            + Add custom model
          </button>
        )}
      </div>

      {/* Your custom models */}
      {(customModels && customModels.length > 0) && (
        <div style={{ marginBottom:40 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16,
            paddingBottom:12, borderBottom:'1px solid var(--bd)' }}>
            <h2 style={{ fontSize:15, fontWeight:800, color:'#0F172A', margin:0 }}>Your custom models</h2>
            <span style={{ fontSize:11, color:'#64748B', fontFamily:'var(--f-mono)' }}>
              {customModels.length} {customModels.length === 1 ? 'model' : 'models'}
            </span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:12 }}>
            {customModels.map(m => (
              <div key={m.id} style={{ background:'#F1F5FB', border:'1px solid var(--bd)', borderRadius:8,
                padding:14, opacity: m.is_active ? 1 : 0.55 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:8 }}>
                  <span style={{ fontFamily:'var(--f-mono)', fontSize:13, fontWeight:600, color:'#0F172A', wordBreak:'break-all' }}>
                    {m.model_name}
                  </span>
                  <PURPOSE_BADGE code={m.family_code}/>
                </div>
                <div style={{ fontSize:11, color:'#64748B', marginTop:6 }}>
                  {m.family_name}{!m.is_active && ' · inactive'}
                </div>
                {m.description && <div style={{ fontSize:12, color:'#475569', marginTop:6 }}>{m.description}</div>}
                <div style={{ fontFamily:'var(--f-mono)', fontSize:10, color:'#94a3b8', marginTop:6, wordBreak:'break-all' }}>
                  {m.source_url}
                </div>
                {canManage && m.is_active && (
                  <button onClick={() => deactivate(m.id)}
                    style={{ marginTop:10, background:'none', border:'1px solid var(--bd)', borderRadius:5,
                      padding:'4px 10px', fontSize:11, color:'#64748B', cursor:'pointer' }}>
                    Deactivate
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add custom model modal */}
      {showAdd && (
        <div onClick={() => !saving && setShowAdd(false)}
          style={{ position:'fixed', inset:0, background:'rgba(15,23,42,.45)', display:'flex',
            alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background:'#fff', borderRadius:10, padding:24, width:460, maxWidth:'100%', maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ margin:'0 0 4px', fontSize:17, fontWeight:800, color:'#0F172A' }}>Add custom model</h3>
            <p style={{ margin:'0 0 16px', fontSize:12, color:'#64748B' }}>
              Register your own base model under an existing family. The family sets the training
              recipe; you provide the identifier and where it's hosted.
            </p>

            <label style={lbl}>Family (recipe)</label>
            <select value={form.family_code} onChange={e => setF('family_code', e.target.value)} style={inp}>
              <option value="">Select a family…</option>
              {(families || []).map(f => (
                <option key={f.family_code} value={f.family_code}>{f.family_name} ({f.family_code})</option>
              ))}
            </select>

            <label style={lbl}>Model identifier</label>
            <input value={form.model_name} onChange={e => setF('model_name', e.target.value)}
              placeholder="e.g. my-org/my-finetuned-model" style={inp}/>

            <label style={lbl}>Source / location</label>
            <input value={form.source_url} onChange={e => setF('source_url', e.target.value)}
              placeholder="URL or path where the weights live" style={inp}/>

            <label style={lbl}>Source type</label>
            <input value={form.source_type} onChange={e => setF('source_type', e.target.value)}
              placeholder="HUGGINGFACE" style={inp}/>

            <label style={lbl}>Description (optional)</label>
            <textarea value={form.description} onChange={e => setF('description', e.target.value)}
              rows={2} style={{ ...inp, resize:'vertical' }}/>

            {formErr && <div style={{ color:'#dc2626', fontSize:12, marginBottom:10 }}>{formErr}</div>}

            <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:8 }}>
              <button onClick={() => setShowAdd(false)} disabled={saving}
                style={{ background:'none', border:'1px solid var(--bd)', borderRadius:6, padding:'8px 14px', fontSize:13, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={submitModel} disabled={saving}
                style={{ background:'#E8470A', color:'#fff', border:'none', borderRadius:6, padding:'8px 16px', fontSize:13, fontWeight:700, cursor:'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Adding…' : 'Add model'}
              </button>
            </div>
          </div>
        </div>
      )}

      {PATH_ORDER.map(path => {
        const items = grouped[path] || [];
        if (!items.length) return null;
        return (
          <div key={path} style={{ marginBottom:48 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16,
              paddingBottom:12, borderBottom:'1px solid var(--bd)' }}>
              <h2 style={{ fontSize:15, fontWeight:800, color:'#0F172A', margin:0 }}>{path}</h2>
              <span style={{ fontSize:11, color:'#64748B', fontFamily:'var(--f-mono)' }}>
                {items.length} {items.length === 1 ? 'family' : 'families'}
              </span>
            </div>

            <div data-tour="catalog-families" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:12 }}>
              {items.map(item => (
                <div key={item.code} style={{ background:'#F1F5FB', border:'1px solid var(--bd)',
                  borderRadius:6, padding:'18px 20px', display:'flex', flexDirection:'column', gap:12 }}>

                  {/* Header */}
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontFamily:'var(--f-mono)', fontSize:11, color:'var(--or)',
                        fontWeight:700, letterSpacing:'.04em', marginBottom:4 }}>
                        {item.code}
                      </div>
                      <div style={{ fontSize:13, fontWeight:700, color:'#0F172A', lineHeight:1.3 }}>
                        {item.headline}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p style={{ fontSize:12, color:'#334155', lineHeight:1.7, margin:0 }}>
                    {item.description}
                  </p>

                  {/* Purposes */}
                  <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                    {(item.purposes || []).map(p => <PURPOSE_BADGE key={p} code={p}/>)}
                  </div>

                  {/* Best for */}
                  <div style={{ borderTop:'1px solid var(--bd)', paddingTop:10 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:'#64748B',
                      textTransform:'uppercase', letterSpacing:'.08em', marginBottom:5 }}>
                      Best for
                    </div>
                    <p style={{ fontSize:12, color:'#334155', lineHeight:1.6, margin:0 }}>
                      {item.bestFor}
                    </p>
                  </div>

                  {/* Examples */}
                  <div>
                    <div style={{ fontSize:10, fontWeight:700, color:'#64748B',
                      textTransform:'uppercase', letterSpacing:'.08em', marginBottom:5 }}>
                      Example uses
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                      {(item.examples || []).map(ex => (
                        <div key={ex} style={{ display:'flex', gap:6, alignItems:'flex-start' }}>
                          <span style={{ color:'var(--or)', fontSize:10, flexShrink:0, marginTop:2 }}>→</span>
                          <span style={{ fontSize:11, color:'#334155', lineHeight:1.5 }}>{ex}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Requirements */}
                  <div style={{ fontSize:10, color:'#64748B', fontFamily:'var(--f-mono)',
                    background:'#E4EAF4', padding:'5px 10px', borderRadius:3,
                    border:'1px solid var(--bd)', marginTop:'auto' }}>
                    {item.requirements}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
